/**
 * Order lifecycle: backend statuses, action verbs, and the
 * selector/menu option arrays the UI renders for each. Ported 1:1
 * from `_legacy/components/Orders/constants.js`.
 *
 * Three layers:
 *   - `ORDER_STATUSES`   — canonical backend status strings (enum).
 *   - `ORDER_ACTIONS`    — canonical action verbs the orders table
 *                          fires (also strings, distinct from
 *                          statuses — e.g. `delay` vs `delayed`).
 *   - `orderStatusesActions` / `ORDER_STATUS_OPTIONS` — derived
 *     option arrays with i18n labels, icons, and per-row capability
 *     flags consumed by the bulk-status menu and the row-actions
 *     popover.
 */
import i18next from 'i18next';
import { ReactComponent as PendingIcon } from 'assets/icons/pending.svg';
import { ReactComponent as DelayedIcon } from 'assets/icons/delayed.svg';
import { ReactComponent as ShippedIcon } from 'assets/icons/shipped.svg';
import { ReactComponent as ProcessingIcon } from 'assets/icons/processing.svg';
import { ReactComponent as CancelledIcon } from 'assets/icons/cancelled.svg';
import { ReactComponent as HoldIcon } from 'assets/icons/hold.svg';

export const ORDER_STATUSES = {
  pending: 'pending',
  processing: 'processing',
  shipped: 'shipped',
  onHold: 'onHold',
  delayed: 'delayed',
  canceled: 'canceled',
  sending: 'sending',
  compounding: 'compounding',
};

export const ORDER_ACTIONS = {
  putOnHold: 'putOnHold',
  revertToPending: 'revertToPending',
  processManually: 'processManually',
  cancel: 'cancel',
  sendToPharmacy: 'sendToPharmacy',
  delay: 'delay',
  ship: 'ship',
};

export const orderStatusesActions = [
  {
    children: i18next.t('tables:putOnHold'),
    value: ORDER_ACTIONS.putOnHold,
    icon: <HoldIcon />,
    allowEditOrder: true,
    enabledForNonSponsors: true,
  },
  {
    children: i18next.t('tables:revertToPending'),
    value: ORDER_ACTIONS.revertToPending,
    icon: <PendingIcon />,
    allowEditOrder: true,
    enabledForNonSponsors: true,
  },
  {
    children: i18next.t('tables:processManually'),
    value: ORDER_ACTIONS.processManually,
    icon: <ProcessingIcon />,
    allowEditOrder: false,
    enabledForNonSponsors: false,
  },
  {
    children: i18next.t('tables:cancel'),
    value: ORDER_ACTIONS.cancel,
    icon: <CancelledIcon />,
    allowEditOrder: false,
    enabledForNonSponsors: true,
  },
  {
    children: i18next.t('tables:delay'),
    value: ORDER_ACTIONS.delay,
    icon: <DelayedIcon />,
    allowEditOrder: true,
    enabledForNonSponsors: false,
  },
  {
    children: i18next.t('tables:ship'),
    value: ORDER_ACTIONS.ship,
    icon: <ShippedIcon />,
    allowEditOrder: false,
    enabledForNonSponsors: false,
  },
];

export const ORDER_STATUS_OPTIONS = [
  // ToDO: edit icon, allowEditOrder and enabledForNonSponsors
  {
    label: i18next.t('tables:sending'),
    value: ORDER_STATUSES.sending,
    icon: PendingIcon,
    allowEditOrder: true,
    enabledForNonSponsors: true,
  },
  {
    label: i18next.t('tables:pending'),
    value: ORDER_STATUSES.pending,
    icon: PendingIcon,
    allowEditOrder: true,
    enabledForNonSponsors: true,
  },
  {
    label: i18next.t('tables:processing'),
    value: ORDER_STATUSES.processing,
    icon: ProcessingIcon,
    allowEditOrder: false,
    enabledForNonSponsors: false,
  },
  {
    label: i18next.t('tables:shipped'),
    value: ORDER_STATUSES.shipped,
    icon: ShippedIcon,
    allowEditOrder: false,
    enabledForNonSponsors: false,
    addDivider: true,
  },
  {
    label: i18next.t('tables:onHold'),
    value: ORDER_STATUSES.onHold,
    icon: HoldIcon,
    allowEditOrder: true,
    enabledForNonSponsors: true,
  },
  {
    label: i18next.t('tables:delayed'),
    value: ORDER_STATUSES.delayed,
    icon: DelayedIcon,
    allowEditOrder: true,
    enabledForNonSponsors: false,
  },
  {
    label: i18next.t('tables:canceled'),
    value: ORDER_STATUSES.canceled,
    icon: CancelledIcon,
    allowEditOrder: false,
    enabledForNonSponsors: true,
  },
  {
    label: i18next.t('tables:compounding'),
    value: ORDER_STATUSES.compounding,
    icon: CancelledIcon,
    allowEditOrder: false,
    enabledForNonSponsors: true,
  },
];
